import { useEffect, useRef } from "react";

const PATH = "/bff/api/users/me/reservation-state/events";
const EVENT_NAME = "reservation-state";
const COALESCE_MS = 50;
const MINIMUM_RECONCILE_INTERVAL_MS = 1_000;
const FALLBACK_REFRESH_MS = 60_000;
const FALLBACK_JITTER_MS = 10_000;
const RETRY_DELAYS_MS = [250, 500, 1_000, 2_000, 5_000] as const;

export interface UserReservationStateEvent {
  facilityId: string;
  revision: number;
}

export interface UseUserReservationStateStreamOptions {
  onReconcile: (
    events: readonly UserReservationStateEvent[] | undefined,
  ) => void | Promise<void>;
  identityKey: string | null;
  enabled?: boolean;
}

function parseEvent(
  event: MessageEvent<string>,
): UserReservationStateEvent | null {
  try {
    const payload = JSON.parse(event.data) as {
      facility_id?: unknown;
      revision?: unknown;
    };
    if (
      typeof payload.facility_id !== "string" ||
      !Number.isSafeInteger(payload.revision) ||
      (payload.revision as number) < 0
    ) {
      return null;
    }
    return {
      facilityId: payload.facility_id,
      revision: payload.revision as number,
    };
  } catch {
    return null;
  }
}

function fallbackDelay(): number {
  return FALLBACK_REFRESH_MS + Math.floor(Math.random() * FALLBACK_JITTER_MS);
}

function retryDelay(attempt: number): number {
  return RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)];
}

/** One authenticated connection reconciles every open player reservation surface. */
export function useUserReservationStateStream({
  onReconcile,
  identityKey,
  enabled = true,
}: UseUserReservationStateStreamOptions): void {
  const reconcileRef = useRef(onReconcile);
  useEffect(() => {
    reconcileRef.current = onReconcile;
  }, [onReconcile]);

  useEffect(() => {
    if (!enabled || !identityKey || typeof EventSource === "undefined") return;

    let active = true;
    let source: EventSource | undefined;
    let coalesceTimer: number | undefined;
    let retryTimer: number | undefined;
    let fallbackTimer: number | undefined;
    let inFlight = false;
    let pendingFallback = false;
    let retryAttempt = 0;
    let lastReconcileStartedAt = Number.NEGATIVE_INFINITY;
    const pending = new Map<string, number>();
    const verified = new Map<string, number>();

    function onEvent(rawEvent: Event): void {
      const event = parseEvent(rawEvent as MessageEvent<string>);
      if (
        event === null ||
        event.revision <= (verified.get(event.facilityId) ?? -1)
      ) {
        return;
      }
      pending.set(
        event.facilityId,
        Math.max(pending.get(event.facilityId) ?? -1, event.revision),
      );
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
        retryTimer = undefined;
      }
      schedule();
    }

    const openSource = (): void => {
      source?.removeEventListener(EVENT_NAME, onEvent);
      source?.close();
      source = new EventSource(PATH);
      source.addEventListener(EVENT_NAME, onEvent);
    };

    const runReconcile = async (): Promise<void> => {
      if (!active || inFlight) return;
      const batch = Array.from(pending, ([facilityId, revision]) => ({
        facilityId,
        revision,
      }));
      const includesFallback = pendingFallback;
      pending.clear();
      pendingFallback = false;
      if (batch.length === 0 && !includesFallback) return;

      inFlight = true;
      lastReconcileStartedAt = performance.now();
      let failed = false;
      try {
        await reconcileRef.current(batch.length > 0 ? batch : undefined);
        for (const event of batch) {
          verified.set(
            event.facilityId,
            Math.max(verified.get(event.facilityId) ?? -1, event.revision),
          );
        }
        retryAttempt = 0;
      } catch {
        failed = true;
        for (const event of batch) {
          if (event.revision > (verified.get(event.facilityId) ?? -1)) {
            pending.set(
              event.facilityId,
              Math.max(pending.get(event.facilityId) ?? -1, event.revision),
            );
          }
        }
        pendingFallback ||= includesFallback;
      } finally {
        inFlight = false;
      }

      if (failed) {
        retryTimer = window.setTimeout(() => {
          retryTimer = undefined;
          schedule();
        }, retryDelay(retryAttempt));
        retryAttempt += 1;
        return;
      }
      schedule();
    };

    const schedule = (): void => {
      if (
        !active ||
        inFlight ||
        coalesceTimer !== undefined ||
        retryTimer !== undefined ||
        (pending.size === 0 && !pendingFallback)
      ) {
        return;
      }
      const sinceLastRun = performance.now() - lastReconcileStartedAt;
      const delay = Math.max(
        COALESCE_MS,
        MINIMUM_RECONCILE_INTERVAL_MS - sinceLastRun,
      );
      coalesceTimer = window.setTimeout(
        () => {
          coalesceTimer = undefined;
          void runReconcile();
        },
        Math.max(0, delay),
      );
    };

    const refreshIfVisible = (): void => {
      if (document.visibilityState !== "visible") return;
      pendingFallback = true;
      schedule();
      openSource();
    };

    const scheduleFallback = (): void => {
      fallbackTimer = window.setTimeout(() => {
        refreshIfVisible();
        scheduleFallback();
      }, fallbackDelay());
    };

    openSource();
    scheduleFallback();
    window.addEventListener("online", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);

    return () => {
      active = false;
      source?.removeEventListener(EVENT_NAME, onEvent);
      source?.close();
      if (coalesceTimer !== undefined) window.clearTimeout(coalesceTimer);
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      window.removeEventListener("online", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [enabled, identityKey]);
}
